import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
} from 'react';
import ListingInactivityWarning from '../components/common/ListingInactivityWarning';
import {
  ListingProvider,
  ListingResource,
  useListingField,
  useListingStatus,
} from '@brightmls/nextgen.lae.flc';
import { useService } from './ServiceContext';
import { useMetadata } from './MetadataContext';
import { navigateTo } from '../utils';
import { KEYSTONE_REDIRECT_URL } from '../config';
import { PropertyField } from '../system/field';
import { useAuth } from './AuthContext';
import { useHotkeys } from 'react-hotkeys-hook';
import { ResoMediaDialog } from '../components/edit/media/ResoMediaDialog';
import { useFeatureFlags } from './FeatureFlagsContext';
import { FeatureFlags } from '../flags';
import { setPreferredPhoto } from '../pages/edit/media/utils';
import { usePublish } from './hooks/usePublish';
import { useExit } from './hooks/useExit';
import { useResoMediaHandler } from './hooks/useResoMediaHandler';
import { useDebugPreferences } from '../hooks/useDebugPreferences';
import { useDebugInfoOptional } from './DebugInfoContext';
import { decompressDebugData } from '../utils/compression';

interface EditListingContextType {
  exit: (hasErrors: boolean, isFromLiveListing: boolean) => void;
  publish: () => void;
}

const EditListingContext = createContext<EditListingContextType | undefined>(
  undefined,
);

export const EditListingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // Get listingKey for hotkey
  const listingKey = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );

  // Ctrl+M hotkey to open Keystone editor
  useHotkeys(
    'ctrl+m',
    () => navigateTo(`${KEYSTONE_REDIRECT_URL}#Edit:${listingKey.value}`, true),
    [listingKey.value],
  );

  // Use extracted hooks
  const { publish } = usePublish();
  const { exit } = useExit(publish);
  const {
    showResoMediaDialog,
    setShowResoMediaDialog,
    fetchResoMedia,
    isFetching,
    isResoMediaHandlingEnabled,
  } = useResoMediaHandler(listingKey.value);

  const contextValue: EditListingContextType = {
    exit,
    publish,
  };

  return (
    <EditListingContext.Provider value={contextValue}>
      {children}
      {isResoMediaHandlingEnabled && (
        <ResoMediaDialog
          open={showResoMediaDialog}
          onClose={() => !isFetching && setShowResoMediaDialog(false)}
          onFetchMedia={fetchResoMedia}
          isFetching={isFetching}
        />
      )}
    </EditListingContext.Provider>
  );
};

export const useEditListing = () => {
  const context = useContext(EditListingContext);
  if (context === undefined) {
    throw new Error(
      'useEditListing must be used within an EditListingProvider',
    );
  }
  return context;
};

export const EditListing: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const metadata = useMetadata();
  const { saveListing, publishListing, getMediaV2SaveHandler } = useService();
  const { hasPermission, isViewOnly, session } = useAuth();
  const { hasFeature, getFeatureValue } = useFeatureFlags();

  // Debug preferences
  const { debugSaves, debugPublish } = useDebugPreferences();
  const debugInfo = useDebugInfoOptional();

  // Check if Media V2 is enabled
  const isMediaV2 = hasFeature(FeatureFlags.NGW_MEDIA_V2);

  // Check if rule skips are enabled: then fetch these
  const isSkipWhenEmptyRules = hasFeature(
    FeatureFlags.NGW_RULE_SKIP_WHEN_EMPTY,
  );
  const skipWhenEmptyRules = isSkipWhenEmptyRules
    ? getFeatureValue(FeatureFlags.NGW_RULE_SKIP_WHEN_EMPTY)
    : undefined;

  // Check if context should be provided to the rule engine with additional values
  const isProvideContext = hasFeature(FeatureFlags.NGW_RULE_PROVIDE_CONTEXT);

  const propertyType = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );

  // Construct the context map with additional values (if enabled)
  const contextMap = isProvideContext
    ? {
        PropertyType: propertyType.value,
        UserClass: session?.user.agent.MemberClass,
        MatrixUserType: session?.user.agent.UserType,
        SystemTranType: 'INSERT',
        MemberMlsSecurityClass: session?.user.agent.MemberClass, // Provide the alias for UserClass (same value)
      }
    : undefined;

  // Hook for publishing status
  const isPublishing = useListingStatus<boolean>('isPublishing', false);

  const handleSave = useCallback(
    async (listing: any, fromPublishStep: boolean = false) => {
      // Skip save for view-only users
      if (isViewOnly()) {
        console.warn('View-only mode, skipping save');
        return {
          ListingKey: listing?.Property?.LISTING_KEY,
          ListingId: listing?.Property?.LISTING_ID,
        };
      }

      if (!fromPublishStep && isPublishing.value) {
        console.error('Cannot save while publishing');

        return {
          ListingKey: listing?.Property?.LISTING_KEY,
          ListingId: listing?.Property?.LISTING_ID,
        };
      }

      // Determine media to save
      let mediaToSave = listing.Media;

      if (isMediaV2) {
        // Media V2: Get only dirty order items from the registered handler
        // If no handler registered or returns empty, save empty array
        const mediaV2Handler = getMediaV2SaveHandler?.();
        mediaToSave = mediaV2Handler?.() ?? [];
      }

      // Media with Preferred Photo set
      const updatedMediaToSave = setPreferredPhoto(mediaToSave);

      const result = await saveListing(
        listing.key ? 'UPDATE' : 'CREATE',
        listing.key,
        {
          Property: listing.Property,
          OpenHouse: listing.OpenHouse,
          Media: updatedMediaToSave,
          Unit: listing.Unit,
          Room: listing.Room,
          GreenVerification: listing.GreenVerification,
        },
        { debug: debugSaves },
      );

      // Store debug info if available (decompress if compressed)
      if (result?._debug && debugInfo) {
        debugInfo.setLastSaveDebug(decompressDebugData(result._debug));
      }

      return result;
    },
    [
      isPublishing.value,
      saveListing,
      isMediaV2,
      getMediaV2SaveHandler,
      isViewOnly,
      debugSaves,
      debugInfo,
    ],
  );

  const handlePublish = async (listing: any) => {
    // Flush any dirty media order from MediaCenter into the listing before publishing
    let mediaToPublish = listing.Media;
    if (isMediaV2) {
      const mediaV2Handler = getMediaV2SaveHandler?.();
      const dirtyMedia = mediaV2Handler?.() ?? [];
      if (dirtyMedia.length > 0) {
        const dirtyMap = new Map(dirtyMedia.map((m: any) => [m.MED_VW_KEY, m]));
        mediaToPublish = listing.Media.map(
          (m: any) => dirtyMap.get(m.MED_VW_KEY) ?? m,
        );
      }
    }

    const result = await publishListing(
      listing.key,
      {
        Property: listing.Property,
        OpenHouse: listing.OpenHouse,
        Media: setPreferredPhoto(mediaToPublish),
        Unit: listing.Unit,
        Room: listing.Room,
        GreenVerification: listing.GreenVerification,
      },
      { debug: debugPublish, mediaV2: isMediaV2 },
    );

    // Store debug info if available (decompress if compressed)
    if (result?._debug && debugInfo) {
      debugInfo.setLastPublishDebug(decompressDebugData(result._debug));
    }

    return result;
  };

  const handleDiscard = async (listing: any) => {
    await saveListing('DELETE', listing.key, {
      Property: listing.Property,
      OpenHouse: listing.OpenHouse,
      Media: listing.Media,
      Unit: listing.Unit,
      Room: listing.Room,
      GreenVerification: listing.GreenVerification,
    });
  };

  return (
    <ListingProvider
      onSave={handleSave}
      onPublish={handlePublish}
      onDiscard={handleDiscard}
      rules={metadata?.rules() || []}
      validationContext={{
        contextMap,
        skipWhenEmpty: skipWhenEmptyRules,
        hasPermission: hasPermission,
        locale: metadata?.locale(),
      }}
    >
      <ListingInactivityWarning />
      <EditListingProvider>{children}</EditListingProvider>
    </ListingProvider>
  );
};
